import { CLIENT_AUTHENTICATION_SERVICE, ClientAuthenticationService } from "@cervval.js/client-auth/src/clientAuthentication.service";
import { Event, EventHandler } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { getElementByRef } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { LoginData, User } from "@cervval.js/common-auth/src/loginData.model";
import { PERMISSION_SERVICE, PermissionService } from "@cervval.js/common-permissions";
import { LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injectable } from "@cervval.js/common/src/dependencies/injectable";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { getVOFValue, objectKeys } from "@cervval.js/common/src/tools/object.tools";
import { UrlTools } from "@cervval.js/common/tools";
import { DefaultMenuHelper, MenuHelper } from "./menu/menu.helper";
import { MenuDefinitions, MenuItemDefinition } from "./menu/menuDefinition";
import { MenuFactories } from "./menu/menuFactory";
import { ENavigationLifeCycle, LifeCycleEvent, NavigationListeners } from "./navigation.lifeCycle";
import { BasePageName, Page } from "./navigation.page";
import { DEFAULT_NAVIGATION_CONTAINER_REF, NAVIGATION_SERVICE, NavigationParameters, NavigationService, NavigationServiceConfiguration, PageDefinitions, SearchParams } from "./navigation.service";
import { isMenuDefinition } from "./navigation.tools";
/**
 * Navigation internal wrapper to ease page manipulation
 */
export interface PageWrapper<BasePageName> {

    pageName: BasePageName;

    /**
     * Promise used to create the Page instance
     */
    importPromise: () => Promise<Page>;

    /**
     * The html file content
     * Use htmlContent or pageFilePath
     */
    htmlElement?: HTMLElement;

    /**
     * Instance of the page - Used to make function calls, and updates
     */
    pageInstance?: Page;
}


// FIXME should extend NavigationServiceConfiguration
/**
 * Parameters to install the navigation service
 */
export interface HashNavigationServiceSettings<PageName extends BasePageName, PermissionContext = void> {

    /**
     * Default page for navigation
     */
    defaultPage?: PageName;

    /**
     * Default error page for navigation
     */
    defaultErrorPage?: PageName;

    /** Menus factories (use to rebuild menu dynamically) */
    menuFactories?: MenuFactories;

    /**
     * Optional: Custom container ref key to insert pages routing (by default pagesContainer is used)
     */
    navigationContainerRefKey: string;

    /**
     * Pages to register in navigationService
     */
    pages: PageDefinitions<PageName>;

    /** All config pages (use to rebuild menu dynamically) */
    allConfigPages: PageDefinitions<PageName>;

    /** All config menus (use to rebuild menu dynamically) */
    allConfigMenus: MenuDefinitions;

    /** MenuHelper manages the menus */
    menuHelper: MenuHelper<BasePageName, PermissionContext>;

    /** Callback to get permission context when building pages or menus */
    getterPermissionContext?: () => PermissionContext | undefined;

    /** Callback to get active modules when building pages or menus, cf. @see MenuDefinition<>.neededModuleNames */
    getterActivatedModules?: () => Set<string>;

    /** If true, pages are removed from dom, otherwise they are simply hidden */
    removeFromDomOnPageHide?: boolean;

    /**
     * If true, do not add default pageName to URL. It is not a recommended configuration and can cause some bugs in navigation and menu display.
     * (can be set in login apps to avoid urls like : xxx/login/#login)
     */
    doNotAddDefaultPageNameHash?: boolean;
}


/**
 * NavigationService class using hash
 * Handles navigation between multiple pages in a single page application
 */
@Injectable(NAVIGATION_SERVICE)
export class HashNavigationService<PageName extends BasePageName, PermissionContext> implements NavigationService<PageName, PermissionContext> {

    @Injected(LOG_SERVICE)
    protected readonly _log!: LogService;

    @Injected(CLIENT_AUTHENTICATION_SERVICE, { optional: true })
    protected _clientAuthService?: ClientAuthenticationService;

    @Injected(PERMISSION_SERVICE)
    protected readonly _permissionService!: PermissionService<LoginData<User>, PermissionContext>;

    /** Before page show event triggered before page lifecycle event */
    @Event()
    public beforePageShow!: EventHandler<{ pageName: PageName }>;

    /** After page show event triggered after page lifecycle event */
    @Event()
    public afterPageShow!: EventHandler<{ pageName: PageName }>;

    /** Before navigation event */
    @Event()
    public beforeNavigation!: EventHandler<{ previous: PageName | undefined, next: PageName }>;

    /**
     * Service global settings
     */
    protected _settings?: HashNavigationServiceSettings<PageName, PermissionContext>;

    /** Current default page, can be different from defaultPage because of permissions */
    public defaultPage?: PageName;

    /**
     * List of registered pages used for navigation service
     */
    protected _authorizedPages = new Set<PageName>();

    /**
     * List of all pages used for navigation service (even pages removed because of permissions)
     */
    protected _allPages: Map<PageName, PageWrapper<PageName>> = new Map();

    /** @inheritdoc */
    public get pageNames(): PageName[] {
        return Array.from(this._authorizedPages);
    }

    /**
     * Get current navigation location hash page value (does not start with #)
     * @description CurrentHash can be different from currentPage, due to the fact that client can ask for an inexistent page,
     * so the hash(currentHash) will change, but not the displayed page(currentPage)
     * Always call the window location to set our current hash
     */
    public get currentHash(): PageName {

        const currentHash = window.location.hash;
        return this.getPageNameFromHash(currentHash);
    }

    /** @inheritdoc */
    public currentPage: PageName | undefined;

    /**
     * Used to store the params given to the uri
     * Filled automatically when params are found in the uri
     * 
     * Reminder: uri parameters are build like the following example
     * http://www.myWebsite.com?param1=value1&param2=value2 (...)
     * 
     * Then, everything after the ' ? ' will be handled as parameters
     * 
     */
    public queryParams: Record<string, string> = {};

    /**
     * @param pageName to get menuItem of
     * @returns MenuItem of page if in pages and has one
     */
    public getPageMenuItem(pageName: PageName): MenuItemDefinition | undefined {
        return this._settings?.pages[pageName]?.menuItem;
    }

    /**
     * Current "page routing container" Html object
     */
    private _navigationContainerHtml: HTMLElement | null = null;

    /** @inheritdoc */
    public configure(config: NavigationServiceConfiguration<PageName, PermissionContext>): void {
        if (this._settings) {
            throw new Error("Configuration was already done");
        }

        // initialize service settings
        this._settings = {
            pages: {} as PageDefinitions<PageName>, // TODO: improve typings and avoid casts
            allConfigPages: { ...config.pages } as PageDefinitions<PageName>,
            allConfigMenus: { ...config.menus },
            defaultPage: config.defaultPage,
            defaultErrorPage: config.defaultErrorPage,
            navigationContainerRefKey: config.navigationContainerRef ?? DEFAULT_NAVIGATION_CONTAINER_REF,
            menuFactories: config.menuFactories,
            menuHelper: config.menuHelper ?? new DefaultMenuHelper(),
            getterPermissionContext: config.getterPermissionContext ? config.getterPermissionContext : undefined,
            getterActivatedModules: config.getterActivatedModules ? config.getterActivatedModules : undefined,
            doNotAddDefaultPageNameHash: config.doNotAddDefaultPageNameHash
        };
        // Subscribe to window url hash change, used to emit event in app and navigate
        this._subscribeToHashChange();

        // Build pages and menus
        this._buildMenusAndPages(this._settings);
    }

    /** Build menus and pages */
    private _buildMenusAndPages(settings: HashNavigationServiceSettings<PageName, PermissionContext>, options?: { isRebuild?: boolean }): void {
        // Get allowed pages
        const pagesCopy = { ...settings.allConfigPages };
        for (const pageName in pagesCopy) {
            settings.pages[pageName] = { ...pagesCopy[pageName] };
        }

        if (!options?.isRebuild) {
            this._wrapPages(settings.pages);
        }

        settings.pages = this.filterPagesOrMenusFromPermissions(settings.pages);

        // Configure menu helper
        settings.menuHelper.showHiddenMenu();
        const menus = this.filterPagesOrMenusFromPermissions({ ...settings.allConfigMenus });

        // Returns if no allowed page
        const pageNames = objectKeys<PageDefinitions<PageName>>(settings.pages);
        if (!pageNames.length) {
            this._log.error("None allowed pages !");
            return;
        }

        // Set current default page
        this.defaultPage = settings.defaultPage;
        if (!this.defaultPage || !pageNames.includes(this.defaultPage)) {
            this.defaultPage = pageNames[0];
        }

        // Redefine menus of menus helper if needed
        if (options?.isRebuild) {
            this._clearAuthorizedPages();
            settings.menuHelper.setMenus(menus);
        } else {
            settings.menuHelper.configure(document.body, menus, settings.menuFactories);
        }

        // Add pages to the navigation service
        this.addPages(settings.pages);

        // Display page for current hash or default page
        this._displayInitPage(options);
    }

    /** @inheritdoc */
    public getUrl(pageName: PageName, params?: NavigationParameters): string {
        return "#" + pageName; // TODO - Add navigation parameters to url
    }

    /** @inheritdoc */
    public getSearchParams(): SearchParams {
        // Create an object to parse and manage the search params in current URL
        const urlParams = new URLSearchParams(window.location.search);

        // Loop through the URL parameters and store them in searchParams
        const searchParams: SearchParams = {};
        urlParams.forEach((value, key) => searchParams[key] = value);
        return searchParams;
    }

    /**
     * Update url with deletion of specified params
     */
    public deleteSearchParams(...keys: string[]): void {
        // Create a new URL object based on the current URL
        const url = new URL(window.location.href);
        // Loop through the provided keys (search param names) to delete them
        for (const key of keys) {
            url.searchParams.delete(key);
        }
        // Update url without triggering a page reload
        window.history.replaceState({}, "", url);
    }

    /**
     * Return the page name included in the hash
     * Exemples: 
     *      classic: #MyPage => MyPage
     *      with params:#MyPage?param1=value1 => MyPage
     * @param hash 
     * @returns 
     */
    public getPageNameFromHash(hash: string): PageName {
        const splitted = hash.split("?");
        if (splitted.length > 1) {
            // If we open a new window with some params in the uri, the current hash is with all the params, so we need to remove them first
            return this._asPageName(splitted[0].substring(1));
        } else {
            return this._asPageName(hash.substring(1));
        }
    }

    /** @inheritdoc */
    public navigate(pageName: PageName, params?: NavigationParameters): void {

        const canNavigate = true; // TODO - Implements user authorization handlers

        if (canNavigate) {
            window.location.hash = "#" + pageName; // Navigate

            // TODO - Add navigation parameters to url
        } else {
            // If the user is not authorized to navigate, log an error (when authorization implemented)
            this._log.error(`User not authorized to navigate to page: ${pageName}`);
        }
    }

    /** @inheritdoc */
    public navigatePrevious(): void {
        window.history.back();
    }

    /** @inheritdoc */
    public navigateNext(): void {
        window.history.forward();
    }

    /** @inheritdoc */
    public addPages(pages: PageDefinitions<PageName>): void {
        if (!this._settings) {
            this._log.error("NavigationService should be configured prior to adding pages (=> menus will not be handled)");
            return;
        }

        for (const page of objectKeys(pages)) {
            this._addPage(page, pages[page]);
        }

        // Insert pages in menus
        this._settings.menuHelper.setupMenus(this, this._log, pages, true);
    }

    /** 
     * @inheritdoc 
     * this function modifies pages or menues depending on given @param elementToFilter
     */
    public filterPagesOrMenusFromPermissions<T extends PageDefinitions | MenuDefinitions>(elementToFilter: T): T {
        const context = this._settings?.getterPermissionContext ? this._settings.getterPermissionContext() : undefined;
        const activeModule = this._settings?.getterActivatedModules ? this._settings.getterActivatedModules() : new Set<string>();

        for (const key in elementToFilter) {
            const permissions = getVOFValue(elementToFilter[key].permissions);
            const neededModuleNames = getVOFValue(elementToFilter[key].neededModuleNames);
            if (neededModuleNames?.length) {
                let hasAllNeededModules = true;
                if (!activeModule.size) {
                    hasAllNeededModules = false;
                }
                else {
                    for (const moduleStringOrArray of neededModuleNames) {
                        if (Array.isArray(moduleStringOrArray)) {
                            // if moduleStringOrArray is an array, pages is active if at least one module in the array is active
                            if (moduleStringOrArray.some(moduleName => activeModule.has(moduleName))) { continue; }
                            else {
                                hasAllNeededModules = false;
                                break;
                            }
                        }
                        else if (!activeModule.has(moduleStringOrArray)) {
                            hasAllNeededModules = false;
                            break;
                        }
                    }
                }

                if (!hasAllNeededModules) {
                    if (isMenuDefinition(elementToFilter[key])) {
                        this._settings?.menuHelper.hideMenuInDom(key, elementToFilter[key]);
                        this._removeMenuFromPages(key);
                    }
                    delete elementToFilter[key];
                    continue;
                }
            }

            if (permissions?.length) {
                let hasAllPermissions = true;
                const loginData: LoginData<User> | undefined = this._clientAuthService?.getLoginData();
                if (loginData) {
                    for (const permStringOrArray of permissions) {
                        // if permStringOrArray is an array, permission is granted if user has at least one the permissions in the array
                        if (!this._permissionService.hasPermission(loginData, permStringOrArray, context, false)) {
                            hasAllPermissions = false;
                            break;
                        }
                    }
                } else {
                    // If there is no loginData and element needs a permission => not reachable
                    hasAllPermissions = false;
                }

                if (!hasAllPermissions) {
                    // If loggedIn user does not have permission to see the element, we remove it from the record
                    if (isMenuDefinition(elementToFilter[key])) {
                        this._settings?.menuHelper.hideMenuInDom(key, elementToFilter[key]);
                        this._removeMenuFromPages(key);
                    }
                    delete elementToFilter[key];
                }
            }
        }
        return elementToFilter;
    }

    /**
     * Add page to the navigation service
     *
     * @param page - PageWrapper to add to registered pages
     */
    private _addPage(pageName: PageName, page: PageDefinitions<PageName>[PageName]): void {

        // Check if the pageKey isn't already added to navigation pages
        if (!this._authorizedPages.has(pageName)) {
            this._wrapPageIfNeeded(pageName, page);
            this._authorizedPages.add(pageName);
        } else {
            this._log.error(`This page : ${pageName}, already exists`);
        }
    }

    /** @inheritdoc */
    public refreshCurrentPage(): void {
        if (this.currentPage) {
            this._performNavigation(this.currentPage);
        }
    }

    /**
     * Show a page
     * @param pageWrapper - Page to show
     */
    protected _showPage(pageWrapper: PageWrapper<PageName>): void {
        // If the page is already loaded in app, append it to the body
        if (pageWrapper.htmlElement) {

            // if page is removedFromDom, either from setting or from rebuilding menus
            if (this._settings?.removeFromDomOnPageHide || !pageWrapper.htmlElement?.parentNode) {
                // Ensure navigation container is always defined
                if (!this._navigationContainerHtml && this._settings) {
                    // Retrieve the html container in DOM
                    this._navigationContainerHtml = getElementByRef(document.body, this._settings.navigationContainerRefKey);
                }

                // Show page html
                // Append page content into navigation container
                this._navigationContainerHtml?.append(pageWrapper.htmlElement);
            }

            if (!this._settings?.removeFromDomOnPageHide) {
                // Show page html (remove style.display = "none")
                pageWrapper.htmlElement.style.removeProperty("display");
            }

        } else { // If page isn't loaded, load the chunk first, then show it
            this._loadPage(pageWrapper);
        }

    }

    /**
     * Hide a page
     * @param pageWrapper - Page to hide
     */
    protected _hidePage(pageWrapper: PageWrapper<PageName>): void {
        if (pageWrapper.htmlElement) {
            if (this._settings?.removeFromDomOnPageHide) {
                // Hide page element, remove it from DOM
                pageWrapper.htmlElement.remove();
            } else {
                // Hide page element
                pageWrapper.htmlElement.style.display = "none";
            }
        }
    }

    /**
     * Get page wrapper from navigationPages Map
     *
     * @param pageName - pageName selector
     */
    protected _getAuthorizedPageWrapper(pageName: PageName): PageWrapper<PageName> | undefined {
        if (pageName && String(pageName) !== "" && this._authorizedPages.has(pageName)) {
            const pageWrapper: PageWrapper<PageName> | undefined = this._allPages.get(pageName);
            if (pageWrapper) {
                return pageWrapper;
            } else {
                this._log.error(`PageName: "${pageName}" not found in registered pages`);
            }
        }
        return undefined;
    }


    /**
     * Split the params from the uri, and fill the queryParams
     * ! Does not follow the conventional uri params writting
     * => conventional: domain?params#hash
     * => cjs: domain#hash?params
     * @see {@link https://developer.mozilla.org/en-US/docs/Learn/Common_questions/Web_mechanics/What_is_a_URL}
     */
    protected _processUrlParams(): void {
        // We use hash to ignore the conventional params, so when we have an uri with the following pattern domain?params#hash?params , it will ignore the first ?params
        const uri = window.location.hash;
        const splitted = uri.split("?"); // Split the params from the hash
        this.queryParams = {};
        if (splitted[1]) {// if params are found
            const params = splitted[1].split("&");// split sub params
            for (const param of params) {
                const splittedParam = param.split("=");// build the key-value association
                if (splittedParam.length === 2) {
                    this.queryParams[splittedParam[0]] = decodeURIComponent(splittedParam[1]);
                } else {
                    this._log.error(`Incorrect URI params: ${param}`);
                }
            }
        }
    }

    /**
     * Get the page instance a selected pageName - use the registered page names
     * @param pageName - pageName selector
     */
    private _getPageInstance(pageName: PageName): Page | undefined {
        const pageWrapper: PageWrapper<PageName> | undefined = this._getAuthorizedPageWrapper(pageName);
        if (pageWrapper?.pageInstance) { // Check if page instance attribute exist for this pageWrapper
            return pageWrapper.pageInstance;
        } else if (!pageWrapper) {
            this._log.error(`Page instance for pageName: "${pageName}" not found in navigationPages list`);
            return undefined;
        } else {
            return undefined;
        }
    }

    /** Subscribe to window onhashchange callback method */
    protected _subscribeToHashChange(): void {
        if (!("onhashchange" in window)) {
            throw new Error("hashchange not handled by browser");
        }

        // Set onHashChangeCallBack
        window.onhashchange = this._onLocationHashChanged.bind(this);
    }

    /**
     * When the window location hash has change
     *
     * @param event - Event returned by the "window.onhashchange" function
     */
    protected _onLocationHashChanged(event: HashChangeEvent): void {
        // Get the 'to' url, to dispatch events and navigate
        let toPageName: PageName = this._asPageName(UrlTools.getHashFromUrl(event.newURL));
        // consider empty hash as the default page
        if (toPageName === "" && this.defaultPage && !this._settings?.doNotAddDefaultPageNameHash) {
            toPageName = this.defaultPage;
            window.history.pushState(null, "", "#" + this.defaultPage);
        }
        this._performNavigation(toPageName);
    }

    /**
     * perform navigation to page (and redirect to error page if needed)
     */
    protected _performNavigation(toPageName: PageName, options?: { isRebuild?: boolean }): void {

        this.beforeNavigation.emit({ previous: this.currentPage, next: toPageName });

        // Fill queryParams if there is params in the uri
        this._processUrlParams();
        // Get the page destination(to) in registered pages
        const toPageWrapper: PageWrapper<PageName> | undefined = this._getAuthorizedPageWrapper(toPageName);

        // If the destination page is registered
        if (toPageWrapper) {

            // Get the current page
            const fromPageWrapper: PageWrapper<PageName> | undefined = this.currentPage && !options?.isRebuild ? this._getAuthorizedPageWrapper(this.currentPage) : undefined;

            // If this page is a registered one, hide it and trigger events
            if (fromPageWrapper) {
                this._dispatchLifeCycleEvent(fromPageWrapper.pageName, ENavigationLifeCycle.BeforePageHide, toPageWrapper.pageName);
                this._hidePage(fromPageWrapper);
                this._dispatchLifeCycleEvent(fromPageWrapper.pageName, ENavigationLifeCycle.AfterPageHide, toPageWrapper.pageName);
            }

            //  After hiding the previous page, display the destination page, update menus and trigger events
            this.beforePageShow.emit({ pageName: toPageWrapper.pageName });
            this._dispatchLifeCycleEvent(toPageWrapper.pageName, ENavigationLifeCycle.BeforePageShow, toPageWrapper.pageName);
            if (this._settings?.menuHelper) {
                this._settings.menuHelper.updateMenus(
                    this, this._log,
                    { previousPage: fromPageWrapper?.pageName, newPage: toPageWrapper.pageName }
                );
            }
            this.currentPage = toPageWrapper.pageName;
            this._showPage(toPageWrapper);

            // Dispatch afterPageShow
            this._dispatchLifeCycleEvent(toPageWrapper.pageName, ENavigationLifeCycle.AfterPageShow, toPageWrapper.pageName);
            this.afterPageShow.emit({ pageName: toPageWrapper.pageName });
        } else { // If the destination page isn't registered
            // Check if the defaultError page is set, redirect to this one
            if (this._settings?.defaultErrorPage) {
                this.navigate(this._settings.defaultErrorPage);
            } else if (this.defaultPage) { // Else redirect to defaultPage
                this.navigate(this.defaultPage);
            }
        }
    }

    /**
     * Dispatch a lifeCycle event to elements of app
     * 
     * @param listeningPage - Page key from where the event was triggered
     * @param navigationLifeCycle - LifeCycle to dispatch
     * @param pageNameAfterNavigation Page destination of navigate
     */
    protected _dispatchLifeCycleEvent(listeningPage: PageName, navigationLifeCycle: ENavigationLifeCycle, pageNameAfterNavigation?: PageName): void {

        const lifeCycleEvent: LifeCycleEvent = this._buildLifeCycleEvent(listeningPage, navigationLifeCycle, pageNameAfterNavigation);

        // Dispatch event to the page
        this._dispatchLifeCycleEventToPage(this._getPageInstance(listeningPage), navigationLifeCycle, lifeCycleEvent);
    }

    /**
     * Dispatch a lifeCycle event to page concerned by the event
     *
     * @param instance - Instance of class to trigger eventHandler, should extend NavigationListeners
     * @param navigationLifeCycle - LifeCycle to dispatch
     * @param lifeCycleEvent - Event to be dispatched
     */
    protected _dispatchLifeCycleEventToPage(instance: NavigationListeners | undefined, navigationLifeCycle: ENavigationLifeCycle, lifeCycleEvent: LifeCycleEvent): void {

        const eventHandler = instance?.[navigationLifeCycle];
        // Check if the page instance exist, and the function inside this class, if ok: call it
        if (eventHandler) {
            eventHandler.emit(lifeCycleEvent);
        }
    }

    /**
     * Load a chunck for a page from his import promise
     *
     * @param pageWrapper - Page to load
     */
    private _loadPage(pageWrapper: PageWrapper<PageName>): Promise<void> {
        if (!this._settings) {
            throw new Error("NavigationService configure was not called");
        }

        return pageWrapper.importPromise()
            .then(pageInstance => {
                // Set page instance of pageWrapper
                pageWrapper.pageInstance = pageInstance;
                return pageInstance.getHtmlElement();
            })
            .then((htmlElement: HTMLElement) => { // Build the page, retrieve the Html object
                pageWrapper.htmlElement = htmlElement;

                if (!this._navigationContainerHtml && this._settings) {
                    // Retrieve the html container in DOM
                    this._navigationContainerHtml = getElementByRef(document.body, this._settings.navigationContainerRefKey);
                }

                // Append page content into navigation container
                this._navigationContainerHtml?.append(pageWrapper.htmlElement);

                // Dispatch beforePageShow (page hasn't been loaded yet, so performNavigation hasn't triggered lifeCycleEvent for this page)
                this.beforePageShow.emit({ pageName: pageWrapper.pageName });
                this._dispatchLifeCycleEvent(pageWrapper.pageName, ENavigationLifeCycle.BeforePageShow, pageWrapper.pageName);

                // Display the page (remove display:none on pageWrapper.htmlElement)
                this._showPage(pageWrapper);

                // Dispatch afterPageShow (page hasn't been loaded yet, so performNavigation hasn't triggered lifeCycleEvent for this page)
                this._dispatchLifeCycleEvent(pageWrapper.pageName, ENavigationLifeCycle.AfterPageShow, pageWrapper.pageName);
                this.afterPageShow.emit({ pageName: pageWrapper.pageName });
            }).catch(this._log.error);
    }

    /**
     * Show default page on app init
     */
    protected _displayInitPage(options?: { isRebuild?: boolean }): void {

        if (!this.defaultPage) {
            this._log.error("Default page should be set when installing NavigationService");
            return;
        }
        // Check if the location hash exist and is referring to a registered page
        if (String(this.currentHash) !== "") {
            this._performNavigation(this.currentHash, options);
        } // Else show the default page
        else {
            this._performNavigation(this.defaultPage, options);
            if (!this._settings?.doNotAddDefaultPageNameHash) {
                window.history.pushState(null, "", "#" + this.defaultPage); // add hash for default page when init display 
            }
        }
    }

    /**
     * Factory of a LifeCycleEvent
     * @param pageName - The page name for which the event is triggered
     * @param navigationLifeCycle - The LifeCycle for which the event is triggered
     * @param pageNameAfterNavigation - The hashChange event himself
     * @returns Object builded
     */
    private _buildLifeCycleEvent(pageName: PageName, navigationLifeCycle: ENavigationLifeCycle, pageNameAfterNavigation?: PageName): LifeCycleEvent {
        return {
            pageName,
            navigationLifeCycle,
            pageNameAfterNavigation
        };
    }

    /**
     * Convert a string to a PageName object
     */
    protected _asPageName(str: string): PageName {
        return str as unknown as PageName;
    }

    /**
     * Convert a PageDefinitions into allPages PageWrapper map
     */
    private _wrapPages(pageDefinitions: PageDefinitions<PageName>): void {
        for (const pageName in pageDefinitions) {
            this._wrapPageIfNeeded(pageName, pageDefinitions[pageName]);
        }
    }

    /**
     * Convert a PageDefinition into a PageWrapper and add to allPages map (if not already in map)
     */
    private _wrapPageIfNeeded(pageName: PageName, pageDefinition: PageDefinitions<PageName>[PageName]): void {
        if (!this._allPages.has(pageName)) {
            const pageWrapper = {
                pageName,
                importPromise: pageDefinition.importPromise
            };
            this._allPages.set(pageName, pageWrapper);
        }
    }

    /** @inheritdoc */
    public toString(): string {
        return `
        [NavigationService]
            | defaultPage: ${this.defaultPage}
            | settings.defaultPage: ${this._settings?.defaultPage}
            | defaultErrorPage: ${this._settings?.defaultErrorPage}
            | currentPage: ${this.currentPage}
            | navigationPages: ${Array.from(this._authorizedPages).join("\n")}
            | navigationPageNamesList: ${this.pageNames}
            | navigationContainerRefKey: ${this._settings?.navigationContainerRefKey}
            | queryParams: ${this.queryParams}
            | currentHash: ${this.currentHash}
            | currentPage: ${this.currentPage}
        `;
    }

    /** @inheritdoc */
    public toStringJSON(): string {
        return JSON.stringify(this);
    }

    /** @inheritdoc */
    public rebuildAppPagesFromPermissions(): void {
        if (!this._settings) {
            throw new Error("Can't rebuild appPages without settings.");
        }
        this._buildMenusAndPages(this._settings, { isRebuild: true });
    }

    /**
     * Clear navigation page expect currentPageName
     */
    private _clearAuthorizedPages(): void {

        this._authorizedPages.forEach(page => {
            this._allPages.get(page)?.htmlElement?.remove();
        });
        this._authorizedPages.clear();
    }

    /** Remove given menu from pages */
    private _removeMenuFromPages(menuUid: string): void {
        if (!this._settings) { return; }
        const pages = this._settings.pages;
        for (const key of objectKeys<PageDefinitions<PageName>>(pages)) {
            const page = pages[key];
            // Do nothing if no menus are declared
            if (!page.menus || !page.menus.length) { continue; }
            else {
                // Otherwise, remove menu that match the given one
                page.menus = page.menus.filter(menu => menu.menuUid !== menuUid);
                // If no menus anymore, delete the page
                if (!page.menus.length) {
                    delete pages[key];
                }
            }
        }
    }
}
